import {
  Document,
  Page,
  Text,
  View,
  Image,
  Link,
  StyleSheet,
} from '@react-pdf/renderer'
import type { CV, DateFormat, CVLanguage } from '../../types/cv'
import { executivePalettes, type ExecutiveColors } from '../../utils/colorPalettes'
import { getCVLabels, getLevelLabel } from '../../utils/cvTranslations'
import { EmailIcon, PhoneIcon, LocationIcon, BirthDateIcon } from './ContactIcons'


function getColors(paletteId?: string): ExecutiveColors {
  return executivePalettes[paletteId || 'navy'] || executivePalettes.navy
}

const styles = StyleSheet.create({
  page: {
    paddingTop: 50,
    paddingBottom: 50,
    paddingHorizontal: 50,
    fontFamily: 'Times-Roman',
    fontSize: 10,
    lineHeight: 1.5,
  },
  header: {
    marginBottom: 25,
    paddingBottom: 20,
    borderBottomWidth: 2,
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 20,
  },
  headerPhoto: {
    width: 85,
    height: 85,
    objectFit: 'cover',
  },
  headerInfo: {
    flex: 1,
  },
  name: {
    fontSize: 28,
    fontFamily: 'Times-Bold',
    letterSpacing: 1,
    marginBottom: 4,
  },
  title: {
    fontSize: 13,
    fontFamily: 'Times-Italic',
    marginBottom: 12,
  },
  contactRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 15,
    fontSize: 9,
  },
  contactItem: {
    flexDirection: 'row',
    alignItems: 'center',
    fontSize: 9,
    textDecoration: 'none',
  },
  contactIcon: {
    fontSize: 8,
    marginRight: 3,
  },
  contactSeparator: {
    fontSize: 9,
  },
  linksRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 12,
    marginTop: 8,
  },
  link: {
    fontSize: 9,
    fontFamily: 'Times-Italic',
  },
  section: {
    marginBottom: 18,
  },
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 10,
    gap: 10,
  },
  sectionTitle: {
    fontSize: 11,
    fontFamily: 'Times-Bold',
    letterSpacing: 2,
    textTransform: 'uppercase',
  },
  sectionLine: {
    flex: 1,
    height: 0.5,
  },
  summaryText: {
    fontSize: 10,
    fontFamily: 'Times-Italic',
    textAlign: 'justify',
  },
  entryContainer: {
    marginBottom: 14,
  },
  entryHeader: {
    marginBottom: 3,
  },
  entryTitleRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'baseline',
  },
  entryTitle: {
    fontSize: 11,
    fontFamily: 'Times-Bold',
  },
  entryDate: {
    fontSize: 9,
    fontFamily: 'Times-Italic',
  },
  entrySubtitle: {
    fontSize: 10,
    marginTop: 2,
  },
  entryLocation: {
    fontSize: 9,
    fontFamily: 'Times-Italic',
    marginTop: 1,
  },
  entryDescription: {
    fontSize: 9,
    marginTop: 5,
    textAlign: 'justify',
  },
  skillsContainer: {
    marginBottom: 18,
  },
  skillsCategory: {
    marginBottom: 8,
  },
  skillsCategoryTitle: {
    fontSize: 9,
    fontFamily: 'Times-Bold',
    marginBottom: 3,
  },
  skillsText: {
    fontSize: 9,
  },
  languagesRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 20,
  },
  languageItem: {
    fontSize: 10,
  },
  languageName: {
    fontFamily: 'Times-Bold',
  },
  languageLevel: {
    fontFamily: 'Times-Italic',
    fontSize: 9,
  },
  decorativeLine: {
    height: 1,
    marginVertical: 3,
  },
})

const localeMap: Record<CVLanguage, string> = {
  es: 'es-ES',
  en: 'en-US',
  fr: 'fr-FR',
  pt: 'pt-PT',
  de: 'de-DE',
}

function formatDate(
  dateStr: string | undefined,
  format: DateFormat = 'month-year',
  language: CVLanguage = 'en',
  presentLabel: string = 'Present'
): string {
  if (!dateStr) return ''
  const str = dateStr.trim().toLowerCase()
  const locale = localeMap[language] || 'en-US'
  if (str.includes('present') || str.includes('actual') || str.includes('current')) {
    return presentLabel
  }
  const yearOnly = format === 'year-only'
  if (/^\d{4}-\d{2}$/.test(dateStr)) {
    const [year, month] = dateStr.split('-')
    if (yearOnly) return year
    const date = new Date(parseInt(year), parseInt(month) - 1)
    if (!isNaN(date.getTime())) {
      return date.toLocaleDateString(locale, { month: 'long', year: 'numeric' })
    }
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    if (yearOnly) return dateStr.split('-')[0]
    const date = new Date(dateStr)
    if (!isNaN(date.getTime())) {
      return date.toLocaleDateString(locale, { month: 'long', year: 'numeric' })
    }
  }
  if (/^\d{4}$/.test(dateStr)) {
    return dateStr
  }
  const mmYYYYMatch = dateStr.match(/^(\d{1,2})[/-](\d{4})$/)
  if (mmYYYYMatch) {
    if (yearOnly) return mmYYYYMatch[2]
    const date = new Date(parseInt(mmYYYYMatch[2]), parseInt(mmYYYYMatch[1]) - 1)
    if (!isNaN(date.getTime())) {
      return date.toLocaleDateString(locale, { month: 'long', year: 'numeric' })
    }
  }
  const yearMatch = dateStr.match(/\b(19|20)\d{2}\b/)
  if (yearMatch) {
    return yearMatch[0]
  }
  return ''
}

function formatDateRange(
  start: string | undefined,
  end: string | undefined,
  current: boolean | undefined,
  format: DateFormat = 'month-year',
  language: CVLanguage = 'en',
  presentLabel: string = 'Present'
): string {
  const startStr = formatDate(start, format, language, presentLabel)
  const endStr = current ? presentLabel : formatDate(end, format, language, presentLabel)
  if (!startStr && !endStr) return ''
  if (!startStr) return endStr
  if (!endStr) return startStr
  return `${startStr} — ${endStr}`
}


interface ExecutiveTemplateProps {
  cv: CV
  colorPalette?: string
}


export function ExecutiveTemplate({ cv, colorPalette }: ExecutiveTemplateProps) {
  const colors = getColors(colorPalette)
  const labels = getCVLabels(cv.settings?.language, cv.settings?.customLabels)
  const showIcons = cv.settings?.showContactIcons ?? true
  const hasPersonalInfo =
    cv.personalInfo?.fullName ||
    cv.personalInfo?.title ||
    cv.personalInfo?.email ||
    cv.personalInfo?.phone ||
    cv.personalInfo?.location

  const hasExperience = cv.experience && cv.experience.length > 0
  const hasEducation = cv.education && cv.education.length > 0
  const hasSkills =
    (cv.skills?.technical && cv.skills.technical.length > 0) ||
    (cv.skills?.soft && cv.skills.soft.length > 0) ||
    (cv.skills?.tools && cv.skills.tools.length > 0)
  const hasLanguages = cv.languages && cv.languages.some((lang) => lang.name)

  return (
    <Document
      title={`CV - ${cv.personalInfo?.fullName || 'Curriculum Vitae'}`}
      author={cv.personalInfo?.fullName || ''}
      subject={cv.personalInfo?.title || 'Curriculum Vitae'}
      creator="CVtoday"
      keywords={`CV, resume, ${cv.personalInfo?.fullName || ''}, ${cv.personalInfo?.title || ''}`}
    >
      <Page size="A4" style={[styles.page, { backgroundColor: colors.background, color: colors.text }]}>
        {/* Header */}
        {hasPersonalInfo && (
          <View style={[styles.header, { borderBottomColor: colors.accent }]}>
            <View style={styles.headerContent}>
              {cv.personalInfo?.photo && (
                <Image
                  src={cv.personalInfo.photo}
                  style={[styles.headerPhoto, { borderWidth: 3, borderColor: colors.accent }]}
                />
              )}
              <View style={styles.headerInfo}>
                {cv.personalInfo?.fullName && (
                  <Text style={[styles.name, { color: colors.primary }]}>
                    {cv.personalInfo.fullName.toUpperCase()}
                  </Text>
                )}
                {cv.personalInfo?.title && (
                  <Text style={[styles.title, { color: colors.textMuted }]}>
                    {cv.personalInfo.title}
                  </Text>
                )}

                <View style={styles.contactRow}>
                  {cv.personalInfo?.email && (
                    <Link src={`mailto:${cv.personalInfo.email}`} style={styles.contactItem}>
                      {showIcons && <EmailIcon size={10} color={colors.text} style={{ marginRight: 4, marginBottom: 1 }} />}
                      <Text style={{ color: colors.text }}>{cv.personalInfo.email}</Text>
                    </Link>
                  )}
                  {cv.personalInfo?.email && cv.personalInfo?.phone && (
                    <Text style={[styles.contactSeparator, { color: colors.accent }]}>|</Text>
                  )}
                  {cv.personalInfo?.phone && (
                    <Link src={`tel:${cv.personalInfo.phone}`} style={styles.contactItem}>
                      {showIcons && <PhoneIcon size={10} color={colors.text} style={{ marginRight: 4, marginBottom: 1 }} />}
                      <Text style={{ color: colors.text }}>{cv.personalInfo.phone}</Text>
                    </Link>
                  )}
                  {(cv.personalInfo?.email || cv.personalInfo?.phone) && cv.personalInfo?.location && (
                    <Text style={[styles.contactSeparator, { color: colors.accent }]}>|</Text>
                  )}
                  {cv.personalInfo?.location && (
                    <View style={styles.contactItem}>
                      {showIcons && <LocationIcon size={10} color={colors.text} style={{ marginRight: 4, marginBottom: 1 }} />}
                      <Text style={{ color: colors.text }}>{cv.personalInfo.location}</Text>
                    </View>
                  )}
                  {(cv.personalInfo?.email || cv.personalInfo?.phone || cv.personalInfo?.location) && cv.personalInfo?.birthDate && (
                    <Text style={[styles.contactSeparator, { color: colors.accent }]}>|</Text>
                  )}
                  {cv.personalInfo?.birthDate && (
                    <View style={styles.contactItem}>
                      {showIcons && <BirthDateIcon size={10} color={colors.text} style={{ marginRight: 4, marginBottom: 1 }} />}
                      <Text style={{ color: colors.text }}>{cv.personalInfo.birthDate}</Text>
                    </View>
                  )}
                </View>

                {cv.personalInfo?.links && cv.personalInfo.links.length > 0 && (
                  <View style={styles.linksRow}>
                    {cv.personalInfo.links.map((link) =>
                      link.url ? (
                        <Link key={link.id} src={link.url} style={[styles.link, { color: colors.accent }]}>
                          {link.label || link.url}
                        </Link>
                      ) : null
                    )}
                  </View>
                )}
              </View>
            </View>
          </View>
        )}

        {/* Summary */}
        {cv.summary && (
          <View style={styles.section}>
            <View style={styles.sectionHeader}>
              <Text style={[styles.sectionTitle, { color: colors.primary }]}>
                {labels.summary}
              </Text>
              <View style={[styles.sectionLine, { backgroundColor: colors.accent }]} />
            </View>
            <Text style={[styles.summaryText, { color: colors.text }]}>
              {cv.summary}
            </Text>
          </View>
        )}

        {/* Experience */}
        {hasExperience && (
          <View style={styles.section}>
            <View style={styles.sectionHeader}>
              <Text style={[styles.sectionTitle, { color: colors.primary }]} minPresenceAhead={40}>
                {labels.experience}
              </Text>
              <View style={[styles.sectionLine, { backgroundColor: colors.accent }]} />
            </View>
            {cv.experience?.map((exp) => (
              <View key={exp.id} style={styles.entryContainer}>
                <View style={styles.entryHeader}>
                  <View style={styles.entryTitleRow}>
                    <Text style={[styles.entryTitle, { color: colors.text }]}>
                      {exp.position || 'Position'}
                    </Text>
                    <Text style={[styles.entryDate, { color: colors.textMuted }]}>
                      {formatDateRange(exp.startDate, exp.endDate, exp.current, cv.settings?.dateFormat, cv.settings?.language, labels.present)}
                    </Text>
                  </View>
                  {exp.company && (
                    <Text style={[styles.entrySubtitle, { color: colors.accent }]}>
                      {exp.company}
                    </Text>
                  )}
                  {exp.location && (
                    <Text style={[styles.entryLocation, { color: colors.textMuted }]}>
                      {exp.location}
                    </Text>
                  )}
                </View>
                {exp.description && (
                  <Text style={[styles.entryDescription, { color: colors.text }]}>
                    {exp.description}
                  </Text>
                )}
              </View>
            ))}
          </View>
        )}

        {/* Education */}
        {hasEducation && (
          <View style={styles.section}>
            <View style={styles.sectionHeader}>
              <Text style={[styles.sectionTitle, { color: colors.primary }]} minPresenceAhead={40}>
                {labels.education}
              </Text>
              <View style={[styles.sectionLine, { backgroundColor: colors.accent }]} />
            </View>
            {cv.education?.map((edu) => (
              <View key={edu.id} style={styles.entryContainer}>
                <View style={styles.entryHeader}>
                  <View style={styles.entryTitleRow}>
                    <Text style={[styles.entryTitle, { color: colors.text }]}>
                      {edu.degree}{edu.field ? ` in ${edu.field}` : ''}
                    </Text>
                    <Text style={[styles.entryDate, { color: colors.textMuted }]}>
                      {formatDateRange(edu.startDate, edu.endDate, edu.current, cv.settings?.dateFormat, cv.settings?.language, labels.present)}
                    </Text>
                  </View>
                  {edu.institution && (
                    <Text style={[styles.entrySubtitle, { color: colors.accent }]}>
                      {edu.institution}
                    </Text>
                  )}
                  {edu.location && (
                    <Text style={[styles.entryLocation, { color: colors.textMuted }]}>
                      {edu.location}
                    </Text>
                  )}
                </View>
                {edu.gpa && (
                  <Text style={[styles.entryDescription, { color: colors.text }]}>
                    {`${labels.gpa}: ${edu.gpa}`}
                  </Text>
                )}
              </View>
            ))}
          </View>
        )}

        {/* Skills */}
        {hasSkills && (
          <View style={styles.skillsContainer} wrap={false}>
            <View style={styles.sectionHeader}>
              <Text style={[styles.sectionTitle, { color: colors.primary }]}>
                {labels.skills}
              </Text>
              <View style={[styles.sectionLine, { backgroundColor: colors.accent }]} />
            </View>

            {cv.skills?.technical && cv.skills.technical.length > 0 && (
              <View style={styles.skillsCategory}>
                <Text style={[styles.skillsCategoryTitle, { color: colors.primary }]}>
                  {labels.technical}:
                </Text>
                <Text style={[styles.skillsText, { color: colors.text }]}>
                  {cv.skills.technical.join('  •  ')}
                </Text>
              </View>
            )}

            {cv.skills?.tools && cv.skills.tools.length > 0 && (
              <View style={styles.skillsCategory}>
                <Text style={[styles.skillsCategoryTitle, { color: colors.primary }]}>
                  {labels.tools}:
                </Text>
                <Text style={[styles.skillsText, { color: colors.text }]}>
                  {cv.skills.tools.join('  •  ')}
                </Text>
              </View>
            )}

            {cv.skills?.soft && cv.skills.soft.length > 0 && (
              <View style={styles.skillsCategory}>
                <Text style={[styles.skillsCategoryTitle, { color: colors.primary }]}>
                  {labels.soft}:
                </Text>
                <Text style={[styles.skillsText, { color: colors.text }]}>
                  {cv.skills.soft.join('  •  ')}
                </Text>
              </View>
            )}
          </View>
        )}

        {/* Languages */}
        {hasLanguages && (
          <View style={styles.section} wrap={false}>
            <View style={styles.sectionHeader}>
              <Text style={[styles.sectionTitle, { color: colors.primary }]}>
                {labels.languages}
              </Text>
              <View style={[styles.sectionLine, { backgroundColor: colors.accent }]} />
            </View>
            <View style={styles.languagesRow}>
              {cv.languages?.map((lang) =>
                lang.name ? (
                  <Text key={lang.id} style={styles.languageItem}>
                    <Text style={[styles.languageName, { color: colors.text }]}>{lang.name}</Text>
                    {lang.level ? (
                      <Text style={[styles.languageLevel, { color: colors.textMuted }]}>
                        {` — ${getLevelLabel(lang.level, cv.settings?.language)}`}
                      </Text>
                    ) : null}
                  </Text>
                ) : null
              )}
            </View>
          </View>
        )}
      </Page>
    </Document>
  )
}
